import * as Turbo from '@hotwired/turbo'
Turbo.start()

import "../controllers"

// Font Awesome
import '@fortawesome/fontawesome-free/js/fontawesome';
import '@fortawesome/fontawesome-free/js/solid';
import '@fortawesome/fontawesome-free/js/regular';
import '@fortawesome/fontawesome-free/js/brands';

// Fonts
import "@fontsource/nunito";                // 400 normal
import "@fontsource/nunito/700.css";        // 700 normal
import "@fontsource/nunito/400-italic.css"; // 400 italic
import "@fontsource/nunito/700-italic.css"; // 700 italic
import "@fontsource/noto-serif-jp/500.css";
import "@fontsource/noto-serif-jp/600.css";
import "@fontsource/noto-serif-jp/700.css";

import '../src/main'
import '../src/cocoon'
import '../src/resume_pdf'

// Import Flowbite modules
import 'flowbite/dist/flowbite.turbo'
import { initFlowbite } from 'flowbite'

import '@/builds/tailwind.css'

// Auto convert from Kanji to Katakana
import process from 'process';
window.process = process;

import Kuroshiro from "kuroshiro";
import KuromojiAnalyzer from "kuroshiro-analyzer-kuromoji";

const kuroshiro = new Kuroshiro();
const delayTime = 500;
let kuroshiroReady = false;

// Define appraiseResume globally so it can be called from inline onclick handlers
window.appraiseResume = () => {
  console.log('🚀 Starting resume appraisal...');

  // Get the modal element
  const appraiseModalEl = document.getElementById('appraise-resume-modal');
  const appraiseCloseButton = document.getElementById('appraise-close-button');
  if (!appraiseModalEl) {
    console.error('Modal element not found');
    return;
  }

  // Initialize Flowbite
  initFlowbite();

  const modalOptions = {
    placement: 'top-center',
    backdrop: 'static',
    closable: true
  };

  const appraiseModal = new Modal(appraiseModalEl, modalOptions);

  // Handle modal close to ensure backdrop is removed
  appraiseCloseButton.addEventListener('click', () => {
    appraiseModal.hide();
    // Remove backdrop manually
    const backdrop = document.querySelector('[modal-backdrop]');
    if (backdrop) {
      backdrop.remove();
    }
  });

  // Show the modal
  appraiseModal.show();
  appraiseCloseButton.disabled = true;

  // Show thinking animation
  toggleAssistantState('greeting');
  setTimeout(() => {
    toggleAssistantState('thinking');
  }, 3000);

  showAppraiseSections(['loading']);

  // Collect and log form data
  const formData = collectResumeFormData();

  // Make API call
  makeAppraiseRequest(formData)
    .then(data => {
      console.log('✅ Appraisal completed:', data);
      toggleAssistantState('showing');
      showAppraiseSections(['success', 'result']);
      document.getElementById('appraise_result').innerHTML = data.result;
    })
    .catch(error => {
      console.error('❌ Appraisal failed:', error);
      toggleAssistantState('apologizing');
      showAppraiseSections(['error']);
    })
    .finally(() => {
      appraiseCloseButton.disabled = false;
    });
};

document.addEventListener('turbo:load', async () => {
  const form = document.querySelector('.resume-form');
  if (!form) return;

  if (!kuroshiroReady) {
    await kuroshiro.init(new KuromojiAnalyzer({ dicPath: "/kuromoji_dict" }));
    kuroshiroReady = true;
  }

  const kanaPairs = document.querySelectorAll('[data-kana-target]');
  kanaPairs.forEach(input => {
    const targetSelector = input.dataset.kanaTarget;
    const output = document.querySelector(targetSelector);
    if (!output) return;

    let lastValue = "";
    let debounceTimer;

    const handleConvert = async () => {
      const text = input.value.trim();
      if (text === "" || text === lastValue) return;
      lastValue = text;

      try {
        const converted = await kuroshiro.convert(text, {
          to: "katakana",
          mode: "spaced"
        });
        output.value = converted;
      } catch (err) {
        console.error("Katakana conversion failed:", err);
      }
    };

    const debouncedConvert = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(handleConvert, delayTime);
    };

    input.addEventListener('input', debouncedConvert);
    input.addEventListener('change', debouncedConvert);
    input.addEventListener('blur', debouncedConvert);

    if (input.value.trim() !== "") {
      debouncedConvert();
    }
  });

  // 💌 Trigger khi nhập postal code hoặc contact_postal_code
  const postalFields = [
    'input[name="resume_form[postal_code]"]',
    'input[name="resume_form[contact_postal_code]"]'
  ];

  postalFields.forEach(selector => {
    const field = document.querySelector(selector);
    if (!field) return;

    let debounce;
    field.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        kanaPairs.forEach(input => {
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }, delayTime);
    });
  });
});

// Collect form data with beautiful logging
function collectResumeFormData() {
  const formData = {};

  // Basic information
  const basicFields = [
    'id', 'name', 'name_kana', 'postal_code', 'address', 'address_kana', 'tel', 'email', 'contact_postal_code', 'contact_address', 'contact_address_kana', 'contact_tel', 'contact_email', 'introduce', 'personal_desires', 'desired_commute_hour', 'desired_commute_minute', 'dependent_count'
  ];

  basicFields.forEach(field => {
    const selector = field === 'address' || field === 'contact_address' || field === 'introduce' || field === 'personal_desires'
      ? `textarea[name="resume_form[${field}]"]`
      : `input[name="resume_form[${field}]"]`;

    const element = document.querySelector(selector);
    formData[field] = element?.value || '';
  });

  // Radio button fields
  const radioButtons = ['gender', 'spouse', 'spouse_obligation'];
  radioButtons.forEach(field => {
    const element = document.querySelector(`input[name="resume_form[${field}]"]:checked`);
    formData[field] = element?.value || '';
  });

  // Date fields
  const dateFields = ['current_date_year', 'current_date_month', 'current_date_day', 'birthday_year', 'birthday_month', 'birthday_day'];
  dateFields.forEach(field => {
    const element = document.querySelector(`select[name="resume_form[${field}]"]`);
    formData[field] = element?.value || '';
  });

  // Collect nested attributes
  formData.education_histories = collectNestedAttributes('education_histories_attributes');
  formData.employment_histories = collectNestedAttributes('employment_histories_attributes');
  formData.certificates = collectNestedAttributes('certificates_attributes');

  return formData;
}

// Collect nested attributes (education, employment, certificates)
function collectNestedAttributes(attributeName) {
  const entries = [];
  const inputs = document.querySelectorAll(`input[name*="${attributeName}"], select[name*="${attributeName}"]`);

  inputs.forEach(input => {
    const match = input.name.match(new RegExp(`${attributeName}\\]\\[(\\d+)\\]\\[(\\w+)\\]`));
    if (match) {
      const index = parseInt(match[1]);
      const field = match[2];

      if (!entries[index]) entries[index] = {};
      entries[index][field] = input.value;
    }
  });

  // Filter out empty entries and log
  const validEntries = entries.filter(entry => entry && entry.content);

  return validEntries;
}

// Make API request
async function makeAppraiseRequest(formData) {
  const response = await fetch('/resumes/appraise', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    },
    body: JSON.stringify({ target_language: getLocale(), resume_form: formData })
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

function showAppraiseSections(sectionIds) {
  const sections = ['loading', 'success', 'error', 'result'];
  sections.forEach(s => {
    const element = document.getElementById(`appraise_${s}`);
    if (element && !sectionIds.includes(s)) {
      element.classList.add('hidden');
    } else if (element) {
      element.classList.remove('hidden');
    }
  });
}

// Assistant state management
function toggleAssistantState(state) {
  const states = ['greeting', 'thinking', 'showing', 'apologizing'];

  states.forEach(s => {
    const element = document.getElementById(`assistant_${s}`);
    if (element) {
      if (s === state) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    }
  });
}

function getLocale() {
  return document.querySelector('html').getAttribute('lang');
}